import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import FormLabel from 'components/atoms/FormLabel';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { StyledFormElementWrapper } from 'theme/styles';
import { IFormElementProps } from 'models/form';
import { Editor, createEditor, Transforms, Element as SlateElement, Node } from 'slate';
import { withReact, useSlate, Slate } from 'slate-react';
import { useTranslations } from 'hooks/useTranslations';
import { countEditorCharacters } from 'components/organisms/HtmlSlate';
import { InputCharCounter } from 'components/atoms/InputCharCounter';
import { Grid } from '@mui/material';
import { getNestedErrorMessage, initialValue, LIST_TYPES, TEXT_ALIGN_TYPES } from './helpers';
import {
  ButtonToolboxStyle,
  IconStyle,
  StyledContainerEditorIcons,
  StyledEditable,
} from './styles';
import { colors } from 'theme/colors';

interface IElement {
  attributes: any;
  children: any;
  element: any;
}
const Element = ({ attributes, children, element }: IElement) => {
  const style = { textAlign: element.align };
  switch (element.type) {
    case 'quote':
      return (
        <blockquote style={style} {...attributes}>
          {children}
        </blockquote>
      );
    case 'bulleted-list':
      return (
        <ul style={style} {...attributes}>
          {children}
        </ul>
      );
    case 'h1':
      return (
        <h1 style={style} {...attributes}>
          {children}
        </h1>
      );
    case 'h2':
      return (
        <h2 style={style} {...attributes}>
          {children}
        </h2>
      );
    case 'list-item':
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      );
    case 'numbered-list':
      return (
        <ol style={style} {...attributes}>
          {children}
        </ol>
      );
    default:
      return (
        <p
          style={{
            ...style,
            margin: 0,
            padding: 0,
            fontSize: 'inherit',
            fontFamily: 'inherit',
            fontWeight: 'inherit',
            lineHeight: 'inherit',
            letterSpacing: 'inherit',
            color: 'inherit',
          }}
          {...attributes}
        >
          {children}
        </p>
      );
  }
};

interface ILeaf {
  attributes: any;
  children: any;
  leaf: any;
}

const Leaf = ({ attributes, children, leaf }: ILeaf) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }
  if (leaf.code) {
    children = <code>{children}</code>;
  }
  if (leaf.italic) {
    children = <em>{children}</em>;
  }
  if (leaf.underline) {
    children = <u>{children}</u>;
  }
  return <span {...attributes}>{children}</span>;
};

const isBlockActive = (editor: Editor, format: string, blockType: string = 'type') => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n: Node) =>
        !Editor.isEditor(n) && SlateElement.isElement(n) && (n as any)[blockType] === format,
    }),
  );
  return !!match;
};

const normalizeSelection = (editor: Editor) => {
  if (editor.selection) {
    const { anchor, focus } = editor.selection;

    if (!Editor.hasPath(editor, anchor.path) || !Editor.hasPath(editor, focus.path)) {
      Transforms.select(editor, {
        anchor: { path: [0, 0], offset: 0 },
        focus: { path: [0, 0], offset: 0 },
      });
    }
  }
};

const isMarkActive = (editor: Editor, format: string) => {
  try {
    normalizeSelection(editor);
    const marks = Editor.marks(editor);
    return marks ? (marks as any)[format] === true : false;
  } catch (error) {
    console.error(error);
    return false;
  }
};

const toggleMark = (editor: Editor, format: string) => {
  const isActive = isMarkActive(editor, format);
  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const toggleBlock = (editor: Editor, format: string) => {
  const isActive = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type',
  );
  const isList = LIST_TYPES.includes(format);
  Transforms.unwrapNodes(editor, {
    match: (n: Node) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes((n as any).type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true,
  });
  let newProperties = {};
  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = {
      align: isActive ? 'undefined' : format,
    };
  } else {
    newProperties = {
      // eslint-disable-next-line no-nested-ternary
      type: isActive ? 'paragraph' : isList ? 'list-item' : format,
    };
  }
  Transforms.setNodes(editor, newProperties);
  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

interface IButton {
  format: string;
  icon: string;
  disabled?: boolean;
}

const BlockButton = ({ format, icon, disabled }: IButton) => {
  const editor = useSlate();
  return (
    <StyledContainerEditorIcons
      active={isBlockActive(
        editor,
        format,
        TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type',
      )}
      reversed={disabled}
    >
      <ButtonToolboxStyle
        active={isBlockActive(
          editor,
          format,
          TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type',
        )}
        onMouseDown={(event: any) => {
          if (!disabled) {
            event.preventDefault();
            toggleBlock(editor, format);
          }
        }}
        reversed={disabled}
      >
        <IconStyle>{icon}</IconStyle>
      </ButtonToolboxStyle>
    </StyledContainerEditorIcons>
  );
};

const MarkButton = ({ format, icon, disabled }: IButton) => {
  const editor = useSlate();
  return (
    <StyledContainerEditorIcons active={isMarkActive(editor, format)} reversed={disabled}>
      <ButtonToolboxStyle
        active={isMarkActive(editor, format)}
        onMouseDown={(event: any) => {
          if (!disabled) {
            event.preventDefault();
            toggleMark(editor, format);
          }
        }}
        reversed={disabled}
      >
        <IconStyle>{icon}</IconStyle>
      </ButtonToolboxStyle>
    </StyledContainerEditorIcons>
  );
};

interface IFormWyswigEditor extends IFormElementProps {
  disabled?: boolean;
  required?: boolean;
  arrayName?: string;
  arrayIndex?: number;
  setValue?: any;
  setError?: any;
  clearErrors?: any;
  fieldName?: string;
  charsLimit?: number;
  withValidation?: boolean;
  withLabel?: boolean;
  showCounter?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  readerMode?: boolean;
  fullContainer?: boolean;
}

export const FormWyswigEditor = ({
  name,
  label,
  control,
  errors,
  setError,
  clearErrors,
  setValue,
  disabled,
  required,
  arrayName,
  arrayIndex,
  fieldName,
  charsLimit,
  withValidation = true,
  withLabel = true,
  placeholder,
  autoFocus = false,
  showCounter = true,
  readerMode = false,
  fullContainer = false,
}: IFormWyswigEditor) => {
  const { t } = useTranslations();
  const renderElement = useCallback((props: any) => <Element {...props} />, []);
  const renderLeaf = useCallback((props: any) => <Leaf {...props} />, []);
  const [editor] = useState(() => withReact(createEditor()));
  const [errorMessage, setErrorMessage] = useState<string>();
  const [editorCharacters, setEditorCharacters] = useState<number>(0);

  const fieldValue = useWatch({
    control,
    name,
  });

  useEffect(() => {
    let isError = false;
    if (name.includes('.') && !name.includes('[') && Object.keys(errors).length > 0) {
      const keys = name.split('.');
      const errMessage = getNestedErrorMessage(errors, keys);
      if (keys && errMessage) {
        isError = true;
        setErrorMessage(errMessage);
      }
    } else {
      const errMessage =
        arrayName && arrayIndex !== undefined && fieldName
          ? errors?.[arrayName]?.[arrayIndex]?.[fieldName]?.message
          : errors[name]?.message;

      isError = true;
      setErrorMessage(errMessage);
    }
    if (!isError) {
      setErrorMessage('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, name]);

  const getValueFormat = useCallback(
    (HtmlValue: string) => {
      let formattedValue = initialValue;
      if (HtmlValue && HtmlValue.length) {
        try {
          formattedValue = JSON.parse(HtmlValue);
        } catch {
          formattedValue = [
            {
              type: 'paragraph',
              children: [{ text: HtmlValue }],
            },
          ];
        }
      }
      return formattedValue;
    },
    [editor],
  );

  useEffect(() => {
    if (charsLimit && fieldValue) {
      const parsedValue = getValueFormat(fieldValue);
      const count = countEditorCharacters(parsedValue, true);
      setEditorCharacters(count);
    }
  }, [fieldValue, charsLimit, getValueFormat]);

  return (
    <StyledFormElementWrapper
      style={{
        marginBottom: readerMode ? '0' : '14px',
        height: fullContainer ? '100%' : 'auto',
      }}
    >
      {withLabel && !readerMode && (
        <FormLabel name={name} label={label} disabled={disabled} required={required} />
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Slate
            editor={editor}
            initialValue={getValueFormat(field.value)}
            onChange={(value) => {
              if (readerMode) return;

              const isAstChange = editor.operations.some((op) => op.type !== 'set_selection');
              if (isAstChange) {
                const content = JSON.stringify(value);
                setValue(name, content);
              }

              const newEditorCharacters = countEditorCharacters(value, true);

              setEditorCharacters(newEditorCharacters);

              if (charsLimit && newEditorCharacters >= charsLimit) {
                setError(name, {
                  message: t('general.field.validation.maxCharacters').replace(
                    '{0}',
                    charsLimit.toString(),
                  ),
                });
              } else {
                clearErrors(name);
              }
            }}
          >
            {!readerMode && (
              <div>
                <Grid container sx={{ gap: '2px', mb: '4px', maxHeight: 'fit-content' }}>
                  <MarkButton disabled={disabled} format="bold" icon="format_bold" />
                  <MarkButton disabled={disabled} format="italic" icon="format_italic" />
                  <MarkButton
                    disabled={disabled}
                    format="underline"
                    icon="format_underlined"
                  />
                  <MarkButton disabled={disabled} format="code" icon="code" />
                  <BlockButton disabled={disabled} format="h1" icon="looks_one" />
                  <BlockButton disabled={disabled} format="h2" icon="looks_two" />
                  <BlockButton disabled={disabled} format="quote" icon="format_quote" />
                  <BlockButton
                    disabled={disabled}
                    format="numbered-list"
                    icon="format_list_numbered"
                  />
                  <BlockButton
                    disabled={disabled}
                    format="bulleted-list"
                    icon="format_list_bulleted"
                  />
                  <BlockButton disabled={disabled} format="left" icon="format_align_left" />
                  <BlockButton
                    disabled={disabled}
                    format="center"
                    icon="format_align_center"
                  />
                  <BlockButton disabled={disabled} format="right" icon="format_align_right" />
                  <BlockButton
                    disabled={disabled}
                    format="justify"
                    icon="format_align_justify"
                  />
                </Grid>
              </div>
            )}

            <StyledEditable
              renderElement={renderElement}
              renderLeaf={renderLeaf}
              readOnly={readerMode || disabled}
              placeholder={!readerMode ? placeholder : undefined}
              renderPlaceholder={({ attributes, children }) => (
                <span
                  {...attributes}
                  style={{
                    ...attributes.style,
                    top: '13px',
                  }}
                >
                  {children}
                </span>
              )}
              spellCheck={!readerMode}
              autoFocus={!readerMode && autoFocus}
              style={{
                overflowWrap: 'anywhere',
                height: fullContainer ? '100%' : 'fit-content',
                borderColor: errorMessage ? colors.error800 : colors.gray400,
                ...(readerMode && {
                  border: 'none',
                  padding: '0',
                  margin: '0',
                  fontSize: '0.875rem',
                  fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                  fontWeight: 400,
                  lineHeight: 1.2,
                  letterSpacing: '0.01071em',
                  color: '#111827',
                  backgroundColor: 'transparent',
                  minHeight: '0',
                  maxHeight: 'fit-content',
                  height: 'fit-content',
                  outline: 'none',
                  boxShadow: 'none',
                  display: 'block',
                  overflow: 'visible',
                }),
              }}
            />
          </Slate>
        )}
      />
      {!readerMode && (withValidation || (showCounter && charsLimit)) && (
        <Grid container>
          <Grid size={{ xs: 8 }}>
            {withValidation && errorMessage && <FormErrorLabel label={errorMessage} />}
          </Grid>
          <Grid size={{ xs: 4 }}>
            {showCounter && charsLimit && (
              <InputCharCounter current={editorCharacters} max={charsLimit} />
            )}
          </Grid>
        </Grid>
      )}
    </StyledFormElementWrapper>
  );
};
